import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import ToggleButton from '@mui/material/ToggleButton';
import Typography from '@mui/material/Typography';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath';
import { ChartsDataProvider, ChartsDataProviderProps } from '@mui/x-charts/ChartsDataProvider';
import { ChartsGrid } from '@mui/x-charts/ChartsGrid';
import { ChartsSurface } from '@mui/x-charts/ChartsSurface';
import { ChartsTooltipContainer, useAxesTooltip } from '@mui/x-charts/ChartsTooltip';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { useLegend, useSeries } from '@mui/x-charts/hooks';
import { HighlightItemIdentifier } from '@mui/x-charts/models';
import { useQuery } from '@tanstack/react-query';
import NotFoundSmall from 'assets/images/notFoundSmall.png';
import { useAtomValue } from 'jotai';
import { chain, isString, sum } from 'lodash';
import { FC, Fragment, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import {
  locationsAccountAtom,
  locationsActivityAtom,
  locationsCountriesAtom,
  locationsProductTypesAtom,
} from 'src/atoms/filters/locations';
import { getProductTypeByCountryOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { CslCmpDeviceLocationsApiFeaturesGetProductTypeByCountryGetProductTypeByCountryRecordV1 } from 'src/clients/locations/Device Locations API/types.gen';
import { Button } from 'src/common/components/Buttons/Button';
import { ColumnGraphSkeleton } from 'src/common/components/Graphs/common/Graphs/components/ColumnGraphSkeleton';
import { ToggleGroup } from 'src/common/components/Inputs/ToggleGroup/ToggleGroup';
import { pxToRem } from 'src/common/utils/fonts';
import { useGetCountryList } from 'src/locations/hooks/useGetCountryList';
import { formatCompactAxisValue } from 'src/locations/utils/formatCompactAxisValue';
import { Routes } from 'src/Routes.constants';
import { palette } from 'src/theme/palette';
import { isNullish } from 'utility-types';

const productTypeLabelMap: Record<string, { label: string; color: string }> = {
  SIM: { label: 'SIM', color: palette.pink[200] },
  ESIM: { label: 'eSIM', color: palette.indigo[200] },
  RSIM: { label: 'rSIM', color: palette.teal[200] },
  ROUTER: { label: 'Router', color: palette.amber[200] },
};

const productTypeTableMap: Record<string, { pathname: Routes; accountSearchKeyName: string }> = {
  SIM: { pathname: Routes.productsSim, accountSearchKeyName: 'accounts' },
  rSIM: { pathname: Routes.productsRsim, accountSearchKeyName: 'accounts' },
  Router: { pathname: Routes.products, accountSearchKeyName: 'account' },
};

const productTypeIds = Object.keys(productTypeLabelMap);

function buildProductGraphData(
  series: string[],
  categories: string[],
  dataItems: CslCmpDeviceLocationsApiFeaturesGetProductTypeByCountryGetProductTypeByCountryRecordV1[],
): { [seriesName: string]: Array<number> } {
  return series.reduce<Record<string, Array<number>>>((acc, seriesName) => {
    acc[seriesName] = [];

    categories.forEach((category) => {
      const valueOfData = dataItems.find((d) => {
        return d.country === category && d.productType === seriesName;
      });

      if (valueOfData?.count === undefined) {
        acc[seriesName].push(0);
      } else {
        acc[seriesName].push(valueOfData.count);
      }
    });

    return acc;
  }, {});
}

const ProductTypeLegend: FC<{
  selectedValue: { seriesId: string; dataIndex: number } | null;
  onChange: (v: { seriesId: string; dataIndex: number } | null) => void;
}> = (props) => {
  const { selectedValue, onChange } = props;

  const legend = useLegend();
  const series = useSeries();

  return (
    <ToggleGroup
      data-testid="locations.productGraph.legend.buttons"
      value={selectedValue ? selectedValue.seriesId : null}
      onChange={(_e, newValue) => {
        if (newValue === null) {
          onChange(null);
        } else {
          const itemIndex = legend.items.findIndex((i) => i.seriesId === newValue);
          const item = itemIndex === -1 ? null : legend.items[itemIndex];

          if (item) {
            onChange({ seriesId: item.seriesId, dataIndex: itemIndex });
          }
        }
      }}
    >
      {legend.items.map((item) => {
        const { color, label, seriesId } = item;

        const dataForSeries = series.bar?.series[seriesId].data;
        const total = sum(dataForSeries);

        return (
          <ToggleButton
            key={item.seriesId}
            sx={{ textTransform: 'none' }}
            value={seriesId}
            data-testid={`locations.productGraph.legend.buttons.${label}`}
          >
            <Stack direction="row" alignItems="center" useFlexGap spacing={10}>
              <Box sx={{ backgroundColor: color, width: '12px', height: '12px' }} />
              {label} ({total})
            </Stack>
          </ToggleButton>
        );
      })}
    </ToggleGroup>
  );
};

const Tooltip: FC = function Tooltip() {
  const axes = useAxesTooltip();
  const totalProducts = axes
    ? axes[0].seriesItems.reduce((acc, s) => {
        if (typeof s.value === 'number') {
          return acc + s.value;
        }
        return acc;
      }, 0)
    : null;

  return (
    <ChartsTooltipContainer
      trigger="item"
      placement="auto"
      anchor="node"
      sx={(theme) => ({
        color: theme.palette.white,
        backgroundColor: alpha(theme.palette.text.primary, 0.7),
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(8),
        fontSize: pxToRem(13),
      })}
    >
      <Box mb={8}>{axes ? `${axes[0].axisFormattedValue} · Total ${totalProducts}` : null}</Box>
      <Stack>
        {axes
          ? axes[0].seriesItems.map((s) => (
              <Stack key={s.seriesId} direction="row" alignItems="center" useFlexGap spacing={6}>
                <Box sx={{ backgroundColor: s.color, width: '12px', height: '12px' }} />
                <Box>{s.formattedLabel}</Box>
                <Box sx={{ marginRight: 0, marginLeft: 'auto' }}>{s.formattedValue}</Box>
              </Stack>
            ))
          : null}
      </Stack>
    </ChartsTooltipContainer>
  );
};

const ChartSelectedSeries: FC<{
  selected: { seriesId: string; dataIndex: number } | null;
  onClear: () => void;
  account: string;
}> = (props) => {
  const { selected, onClear, account } = props;
  const { t } = useTranslation();

  const legend = useLegend();
  const series = useSeries();

  if (selected === null) {
    return null;
  }

  const item = legend.items.find((i) => i.seriesId === selected.seriesId);

  if (item) {
    const dataForSeries = series.bar?.series[item.seriesId].data;
    const total = sum(dataForSeries);

    return (
      <Stack
        data-testid={`locations.productGraph.selectedSeries.${item.label}`}
        direction="row"
        useFlexGap
        spacing={10}
        alignItems="center"
        justifyContent="space-between"
        sx={(t) => ({
          backgroundColor: t.palette.blue[100],
          borderStyle: 'solid',
          borderWidth: '1px',
          borderColor: t.palette.blue[200],
          borderRadius: '12px',
          paddingY: 8,
          paddingX: 12,
        })}
      >
        <Stack
          data-testid={`locations.productGraph.selectedSeries.${item.label}.message`}
          direction="row"
          useFlexGap
          spacing={6}
          alignItems="center"
        >
          <Box sx={{ backgroundColor: item.color, width: '12px', height: '12px' }} />
          {t('locations.graph.selectedSeries.message', { count: total, series: item.label })}
        </Stack>
        <Stack direction="row" useFlexGap spacing={10} alignItems="center">
          <Button
            data-testid={`locations.productGraph.selectedSeries.${item.label}.clearButton`}
            onClick={onClear}
            variant="tertiary"
            sx={{ flexGrow: 1, flexShrink: 0 }}
          >
            {t('locations.graph.selectedSeries.clearSelection')}
          </Button>
          <Link
            data-testid={`locations.productGraph.selectedSeries.${item.label}.viewLink`}
            to={{
              pathname: productTypeTableMap[item.label].pathname,
              search: `${productTypeTableMap[item.label].accountSearchKeyName}=${account}`,
            }}
          >
            <Button
              data-testid={`locations.productGraph.selectedSeries.${item.label}.viewButton`}
              variant="secondary"
              iconEnd={<ArrowForwardIcon />}
              sx={{ flexGrow: 1, flexShrink: 0 }}
            >
              {t('locations.graph.selectedSeries.viewDataTable', { series: item.label })}
            </Button>
          </Link>
        </Stack>
      </Stack>
    );
  }

  return null;
};

const getCountrySize = (size: number) => {
  const MEDIUM_COUNTRIES = 5;
  const LARGE_COUNTRIES = 15;

  if (size > LARGE_COUNTRIES) {
    return 'large';
  }

  if (size > MEDIUM_COUNTRIES) {
    return 'medium';
  }

  return 'small';
};

const ProductTypeGraph: FC<{
  account: string;
  data: {
    countries: { short: string[]; long: string[]; length: number };
    productTypes: string[];
    dataByType: Record<string, number[]>;
  };
}> = (props) => {
  const { account, data } = props;
  const { productTypes } = data;

  const clipPathId = useId();

  const [selectedSeriesId, setSelectedSeriesId] = useState<{
    seriesId: string;
    dataIndex: number;
  } | null>(null);
  const [highlightedHover, setHighlightedHover] = useState<HighlightItemIdentifier<'bar'> | null>(
    null,
  );
  const seriesToHighlight = highlightedHover || selectedSeriesId;
  const isSingleCountry = data.countries.length === 1;
  const countrySize = getCountrySize(data.countries.length);
  const height = 200 + data.countries.length * 40;

  const commonChartsDataProviderProps: ChartsDataProviderProps = {
    series: productTypes.map((l) => ({
      type: 'bar',
      color: productTypeLabelMap[l].color,
      data: data.dataByType[l],
      label: productTypeLabelMap[l].label,
      stack: isSingleCountry ? undefined : 'all',
      layout: 'horizontal',
      highlightScope: {
        highlight: 'series',
        fade: 'global',
      },
    })),
    highlightedItem: seriesToHighlight,
    onHighlightChange: setHighlightedHover,
    yAxis: [
      {
        width: 'auto',
        scaleType: 'band',
        data:
          countrySize === 'medium' || countrySize === 'large'
            ? data.countries.short
            : data.countries.long,
      },
    ],
  };

  const xAxisConfig = [{ tickMinStep: 1, valueFormatter: formatCompactAxisValue }];

  return (
    <>
      <ChartsDataProvider
        {...commonChartsDataProviderProps}
        height={height}
        margin={{ left: 0, bottom: 0 }}
        xAxis={[{ ...xAxisConfig[0], position: 'none' }]}
      >
        <Stack direction="row" justifyContent="space-between">
          <LocationsGraphSubtitle account={account} />
          <ProductTypeLegend selectedValue={selectedSeriesId} onChange={setSelectedSeriesId} />
        </Stack>
        <Box
          maxHeight={countrySize === 'large' ? '80vh' : 'none'}
          sx={{ position: 'relative', overflowY: 'auto' }}
        >
          <ChartsSurface>
            <ChartsClipPath id={clipPathId} />
            <ChartsGrid horizontal />
            <g clipPath={`url(#${clipPathId})`}>
              <BarPlot borderRadius={4} />
            </g>
            <ChartsXAxis />
            <ChartsYAxis />
          </ChartsSurface>
        </Box>
        <Tooltip />
      </ChartsDataProvider>
      <ChartsDataProvider
        {...commonChartsDataProviderProps}
        height={50}
        xAxis={[{ ...xAxisConfig[0], position: 'bottom' }]}
        margin={{ top: 0, left: 0 }}
      >
        <ChartsSurface>
          <ChartsXAxis />
        </ChartsSurface>
        <ChartSelectedSeries
          selected={selectedSeriesId}
          onClear={() => {
            setSelectedSeriesId(null);
          }}
          account={account}
        />
      </ChartsDataProvider>
    </>
  );
};

export const ProductTypeGraphFetcher: FC<{ account: string }> = (props) => {
  const { account } = props;
  const { t } = useTranslation();
  const countries = useAtomValue(locationsCountriesAtom);
  const productTypes = useAtomValue(locationsProductTypesAtom);
  const activity = useAtomValue(locationsActivityAtom);

  const options = getProductTypeByCountryOptions({
    query: {
      countries,
      accountReferenceId: account,
      productTypes,
      ...(activity && { activityDays: activity }),
    },
  });

  const { data: graphData, status: graphDataStatus } = useQuery({
    ...options,
    meta: { isLocations: true },
  });

  const { data: countryData, status: countryDataStatus } = useGetCountryList({
    accountReferenceId: account,
  });

  if (graphDataStatus === 'error' || countryDataStatus === 'error') {
    return (
      <Box>
        <LocationsGraphTitle />
        <Stack mt="1em" minHeight="9em" justifyContent="center" alignItems="center">
          <Typography color="textSecondary">{t('common.somethingWrong')}</Typography>
        </Stack>
      </Box>
    );
  }

  if (graphDataStatus === 'pending' || countryDataStatus === 'pending') {
    return (
      <Fragment>
        <Skeleton height="1.2em" sx={{ marginBottom: 10 }}>
          <LocationsGraphTitle />
        </Skeleton>
        <ColumnGraphSkeleton />
      </Fragment>
    );
  }

  if (graphData.length === 0) {
    return (
      <Typography textAlign="center" variant="subtitle1">
        {t('locations.graph.noData')}
      </Typography>
    );
  }

  const countriesInData = chain(graphData)
    .map((item) => {
      return countryData.find((c) => c.countryCode === item.country);
    })
    .filter((c) => !isNullish(c))
    .sort((a, b) => a.countryName!.localeCompare(b.countryName!));

  const hasNoLocationCountry = graphData.some((c) => c.country === '');

  const baseUniqueCountryNames = countriesInData
    .map((c) => c.countryName)
    .filter((c): c is string => isString(c) && c !== '')
    .sortedUniq()
    .value();

  const baseUniqueCountryCodes = countriesInData
    .map((c) => c.countryCode)
    .filter((c): c is string => isString(c) && c !== '')
    .sortedUniq()
    .value();

  const uniqueCountryNames = hasNoLocationCountry
    ? [...baseUniqueCountryNames, t('locations.graph.noLocations')]
    : baseUniqueCountryNames;

  const uniqueCountryCodes = hasNoLocationCountry
    ? [...baseUniqueCountryCodes, t('locations.graph.noLocationsShort')]
    : baseUniqueCountryCodes;

  const dataByType = graphData
    ? buildProductGraphData(
        productTypeIds,
        uniqueCountryCodes,
        graphData.map((i) => {
          if (i.country === '') {
            return { ...i, country: t('locations.graph.noLocationsShort') };
          } else {
            return i;
          }
        }),
      )
    : {};

  const uniqueProductTypes = chain(graphData)
    .map((i) => i.productType)
    .filter((i) => isString(i))
    .uniq()
    .value();

  return (
    <Fragment>
      <LocationsGraphTitle />
      <ProductTypeGraph
        account={account}
        data={{
          countries: {
            short: uniqueCountryCodes,
            long: uniqueCountryNames,
            length: uniqueCountryNames.length,
          },
          dataByType,
          productTypes: uniqueProductTypes,
        }}
      />
    </Fragment>
  );
};

const LocationsGraphTitle: FC = () => {
  const { t } = useTranslation();

  return (
    <Typography
      data-testid="locations.productGraph.title"
      variant="h3"
      color="text.secondary"
      sx={{ height: pxToRem(18) }}
      gutterBottom
    >
      {t('locations.graph.title')}
    </Typography>
  );
};

const LocationsGraphSubtitle: FC<{ account: string }> = (props) => {
  const { account } = props;

  const { t } = useTranslation();
  const countries = useAtomValue(locationsCountriesAtom);
  const productTypes = useAtomValue(locationsProductTypesAtom);
  const activity = useAtomValue(locationsActivityAtom);

  const { data, status } = useQuery({
    ...getProductTypeByCountryOptions({
      query: {
        countries,
        accountReferenceId: account,
        productTypes,
        ...(activity && { activityDays: activity }),
      },
    }),
    meta: { isLocations: true },
  });

  if (status === 'error') {
    return;
  }

  if (status === 'pending') {
    return;
  }

  const uniqueCountries = data
    ? Array.from(new Set(data.map((i) => i.country).filter((i) => isString(i))))
    : [];

  const count = uniqueCountries.length;

  return (
    <Typography
      data-testid="locations.productGraph.subtitle"
      variant="text4"
      color="text.secondary"
      gutterBottom
    >
      {count} {t('locations.graph.subtitle', { count })}
    </Typography>
  );
};

const LocationsGraphSelect: FC = () => {
  const { t } = useTranslation();

  return (
    <>
      <LocationsGraphTitle />
      <Typography variant="text1" color="text.secondary" gutterBottom>
        {t('locations.map.selectAnAccount')}
      </Typography>
      <Stack useFlexGap spacing={10} alignItems="center">
        <Box maxWidth="256px">
          <img src={NotFoundSmall} alt="" />
        </Box>
        <Typography variant="h3" color="text.secondary">
          {t('locations.map.selectAnAccount')}
        </Typography>
      </Stack>
    </>
  );
};

export const LocationsGraph: FC = () => {
  const account = useAtomValue(locationsAccountAtom);

  return (
    <Card data-testid="locations.productGraph" sx={{ height: '100%' }}>
      <CardContent
        sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        {account === null ? (
          <LocationsGraphSelect />
        ) : (
          <ProductTypeGraphFetcher account={account} />
        )}
      </CardContent>
    </Card>
  );
};
